import axios from 'axios';

// Authentication utility for managing tokens and login data
class AuthService {
  constructor() {
    this.apiBaseUrl = 'https://api.kewaar.com/api/admin';
    this.setupAxiosInterceptors();
  }

  // Setup Axios interceptors for automatic token handling
  setupAxiosInterceptors() {
    // Request interceptor to add token
    axios.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token && config.url?.includes('api.kewaar.com')) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle token refresh
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401 && error.config && !error.config._retry) {
          error.config._retry = true;
          
          const refreshed = await this.refreshToken();
          if (refreshed) {
            const token = this.getToken();
            error.config.headers.Authorization = `Bearer ${token}`;
            return axios.request(error.config);
          } else {
            this.logout();
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Process login response and store data
  processLoginResponse(loginData) {
    if (loginData && loginData.success && loginData.data) {
      const { user, token, refresh_token, expires_at, refresh_expires_at } = loginData.data;
      
      // Store authentication data
      localStorage.setItem('token', token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('token_expires_at', expires_at);
      localStorage.setItem('refresh_expires_at', refresh_expires_at);
      
      // Store user data
      localStorage.setItem('user_info', JSON.stringify(user));
      
      // Store sidebar data if available
      if (user.sidebar_items && user.sidebar_items.length > 0) {
        localStorage.setItem('sidebar_data', JSON.stringify(user.sidebar_items));
      }
      
      // Set user preferences if available
      if (user.preferences) {
        try {
          const preferences = typeof user.preferences === 'string' 
            ? JSON.parse(user.preferences) 
            : user.preferences;
          
          // Set theme preference
          if (preferences.theme) {
            localStorage.setItem('theme', preferences.theme);
            document.documentElement.setAttribute('data-theme', preferences.theme);
          }
          
          // Store other preferences
          localStorage.setItem('user_preferences', JSON.stringify(preferences));
        } catch (error) {
          console.error('Error parsing user preferences:', error);
        }
      }
      
      return {
        user,
        token,
        sidebarData: user.sidebar_items || []
      };
    }
    
    throw new Error('Invalid login response');
  }

  // Get current token
  getToken() {
    return localStorage.getItem('token');
  }

  // Get refresh token
  getRefreshToken() {
    return localStorage.getItem('refresh_token');
  }

  // Get user info
  getUserInfo() {
    const userInfo = localStorage.getItem('user_info');
    return userInfo ? JSON.parse(userInfo) : null;
  }

  // Get sidebar data
  getSidebarData() {
    const sidebarData = localStorage.getItem('sidebar_data');
    return sidebarData ? JSON.parse(sidebarData) : [];
  }

  // Check if token is expired or will expire soon
  isTokenExpiringSoon(minutesBuffer = 5) {
    const expiryTime = localStorage.getItem('token_expires_at');
    if (!expiryTime) return true;
    
    const expiry = new Date(expiryTime).getTime();
    const now = new Date().getTime();
    const buffer = minutesBuffer * 60 * 1000;
    
    return (expiry - now) < buffer;
  }

  // Refresh authentication token
  async refreshToken() {
    try {
      const refreshToken = this.getRefreshToken();
      const currentToken = this.getToken();
      
      if (!refreshToken || !currentToken) {
        return false;
      }

      const response = await axios.post(`${this.apiBaseUrl}/refresh-with-refresh-token`, {
        refresh_token: refreshToken
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        }
      });

      if (response.data && response.data.success) {
        const { token, refresh_token, expires_at, refresh_expires_at } = response.data.data;
        
        // Update stored tokens
        localStorage.setItem('token', token);
        localStorage.setItem('refresh_token', refresh_token);
        localStorage.setItem('token_expires_at', expires_at);
        localStorage.setItem('refresh_expires_at', refresh_expires_at);
        
        console.log('Token refreshed successfully');
        return true;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
    }
    
    return false;
  }

  // Setup automatic token refresh
  startTokenRefreshTimer() {
    // Check token every 2 minutes
    const interval = setInterval(async () => {
      if (this.isTokenExpiringSoon()) {
        const refreshed = await this.refreshToken();
        if (!refreshed) {
          this.logout();
        }
      }
    }, 2 * 60 * 1000);

    return interval;
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = this.getToken();
    const userInfo = this.getUserInfo();
    return !!(token && userInfo);
  }

  // Get user initials
  getUserInitials() {
    const user = this.getUserInfo();
    if (user && user.first_name && user.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    return 'AU';
  }

  // Logout user
  logout() {
    // Clear all stored data
    localStorage.clear();
    
    // Redirect to login page
    window.location.href = '/login';
  }

  // Get user permissions
  getUserPermissions() {
    const user = this.getUserInfo();
    return user?.permissions?.map(p => p.name) || [];
  }

  // Get user roles
  getUserRoles() {
    const user = this.getUserInfo();
    return user?.roles?.map(r => r.name) || [];
  }

  // Check if user has specific permission
  hasPermission(permission) {
    const permissions = this.getUserPermissions();
    return permissions.includes(permission);
  }

  // Check if user has specific role
  hasRole(role) {
    const roles = this.getUserRoles();
    return roles.includes(role);
  }

  // Update user profile data
  updateUserInfo(userData) {
    const currentUser = this.getUserInfo();
    if (currentUser) {
      const updatedUser = { ...currentUser, ...userData };
      localStorage.setItem('user_info', JSON.stringify(updatedUser));
      return updatedUser;
    }
    return null;
  }
}

// Create singleton instance
const authService = new AuthService();

export default authService; 